import React, { useState } from "react";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../lib/cart-context";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, totals, clear } = useCart();
  const [checking, setChecking] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setChecking(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product.id,
          variation_id: it.product.variation_id,
          quantity: it.quantity,
        })),
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't start checkout. Try again.");
      setChecking(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l border-[#27272A] bg-[#0a0a0a] text-[#F5F5DC] sm:max-w-md"
      >
        <SheetHeader className="border-b border-[#27272A] pb-4">
          <SheetTitle className="font-display text-3xl tracking-widest text-[#F5F5DC]">
            Your <span className="text-[#E11D2A]">Cart</span>
          </SheetTitle>
          <SheetDescription className="text-[#A1A1AA]">
            {totals.count} {totals.count === 1 ? "item" : "items"} ready to roll.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {items.length === 0 ? (
            <div
              data-testid="cart-empty"
              className="flex h-full flex-col items-center justify-center text-center text-[#A1A1AA]"
            >
              <p>Your cart is empty.</p>
              <p className="mt-1 text-xs uppercase tracking-widest">Time to shop!</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => (
                <li
                  key={it.product.id}
                  data-testid={`cart-line-${it.product.id}`}
                  className="flex gap-3 border border-[#27272A] bg-[#121212] p-3"
                >
                  <div
                    className="h-20 w-20 flex-none bg-cover bg-center"
                    style={{ backgroundImage: `url(${it.product.image})` }}
                  />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display text-base tracking-wider text-[#F5F5DC]">
                          {it.product.name}
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                          {it.product.category_label}
                        </div>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.product.id}`}
                        onClick={() => removeItem(it.product.id)}
                        className="text-[#A1A1AA] hover:text-[#E11D2A]"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-2 border border-[#27272A]">
                        <button
                          data-testid={`cart-decrement-${it.product.id}`}
                          onClick={() => updateQty(it.product.id, it.quantity - 1)}
                          className="px-2 py-1 hover:text-[#E11D2A]"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="min-w-[2ch] text-center text-sm">{it.quantity}</span>
                        <button
                          data-testid={`cart-increment-${it.product.id}`}
                          onClick={() => updateQty(it.product.id, it.quantity + 1)}
                          className="px-2 py-1 hover:text-[#E11D2A]"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="font-display text-lg tracking-wider text-[#E11D2A]">
                        ${(it.product.price * it.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-[#27272A] pt-4">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Subtotal</span>
            <span
              data-testid="cart-subtotal"
              className="font-display text-2xl tracking-wider text-[#F5F5DC]"
            >
              ${totals.subtotal.toFixed(2)}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Button
              data-testid="cart-checkout-button"
              disabled={items.length === 0 || checking}
              onClick={checkout}
              className="rounded-none bg-[#E11D2A] py-6 font-semibold uppercase tracking-widest text-white hover:bg-[#F02D3A] disabled:opacity-60"
            >
              {checking ? "Redirecting..." : "Checkout with Square"}
            </Button>
            {items.length > 0 && (
              <button
                data-testid="cart-clear-button"
                onClick={clear}
                className="text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-[#E11D2A]"
              >
                Clear cart
              </button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
